import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FileText, MessageSquare, Calendar as CalendarIcon, LogOut, Bell, User, TrendingUp, BookOpen, Star, FolderOpen, Settings, ChevronDown, HelpCircle, Lock, Menu, X, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { API } from '../config/api';
import { formatDateTimeShort } from '../utils/dateFormat';

function Navigation({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const userMenuRef = useRef(null);
  const notificationsRef = useRef(null);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path);

  // Fetch notifications
  const fetchNotifications = async () => {
    setLoadingNotifications(true);
    try {
      const response = await fetch(`${API}/notifications`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${API}/notifications/${notificationId}/read`, {
        method: 'PATCH',
        credentials: 'include'
      });
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n)
      );
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter(n => !n.read);
      await Promise.all(
        unreadNotifications.map(n =>
          fetch(`${API}/notifications/${n.notification_id}/read`, {
            method: 'PATCH',
            credentials: 'include'
          })
        )
      );
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      await markAsRead(notification.notification_id);
    }
    if (notification.link) {
      navigate(notification.link);
    }
    setShowNotifications(false);
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch notifications on mount and periodically
  useEffect(() => {
    fetchNotifications();
    // Refresh notifications every 60 seconds
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setShowMobileMenu(false);
  }, [location.pathname]);

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: FileText, roles: ['client', 'staff', 'admin'] },
    { path: '/documents', label: 'Documents', icon: FolderOpen, roles: ['client', 'staff', 'admin'] },
    { path: '/messages', label: 'Messages', icon: MessageSquare, roles: ['client', 'staff', 'admin'] },
    { path: '/calendar', label: 'Calendar', icon: CalendarIcon, roles: ['client', 'staff', 'admin'] },
    { path: '/resources', label: 'Resources', icon: BookOpen, roles: ['client'] },
    { path: '/help', label: 'Help', icon: HelpCircle, roles: ['client'] },
    { path: '/analytics', label: 'Analytics', icon: TrendingUp, roles: ['admin'] },
  ];

  const visibleItems = navItems.filter(item => item.roles.includes(user.role));

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Mobile Menu Button */}
          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="lg:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100"
            data-testid="mobile-menu-button"
          >
            {showMobileMenu ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>

          {/* Logo */}
          <button 
            onClick={() => navigate('/dashboard')}
            className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
            data-testid="logo-button"
          >
            <img src="/logo.png" alt="The Ramirez Legal Group" className="h-8 w-8 sm:h-10 sm:w-10 object-contain" />
            <div className="hidden sm:block">
              <span className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Roboto', sans-serif" }}>The Ramirez Legal Group</span>
              <span className="block text-xs text-gray-500 -mt-1">
                {user.role === 'admin' ? 'Admin Portal' : user.role === 'staff' ? 'Staff Portal' : 'Client Portal'}
              </span>
            </div>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1" data-testid="desktop-nav">
            {visibleItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
                    active
                      ? 'bg-[#80298F] text-white'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-[#80298F]'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              );
            })}
          </nav>

          {/* User Menu */}
          <div className="flex items-center space-x-3">
            {/* Notifications Dropdown */}
            <div className="relative" ref={notificationsRef}>
              <Button 
                variant="ghost" 
                size="sm" 
                className="relative" 
                data-testid="notifications-button"
                onClick={() => {
                  setShowNotifications(!showNotifications);
                  if (!showNotifications) {
                    fetchNotifications();
                  }
                }}
              >
                <Bell className="h-5 w-5" />
                {notifications.filter(n => !n.read).length > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-[#DC3239] rounded-full text-white text-xs font-bold flex items-center justify-center">
                    {notifications.filter(n => !n.read).length > 9 ? '9+' : notifications.filter(n => !n.read).length}
                  </span>
                )}
              </Button>

              {/* Notifications Dropdown Menu */}
              {showNotifications && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 max-h-96 overflow-y-auto">
                  <div className="px-4 py-3 border-b border-gray-100 flex justify-between items-center">
                    <p className="text-sm font-semibold text-gray-900">Notifications</p>
                    {notifications.filter(n => !n.read).length > 0 && (
                      <button
                        onClick={markAllAsRead}
                        className="text-xs text-[#80298F] font-medium hover:underline"
                        data-testid="mark-all-read-button"
                      >
                        Mark all as read
                      </button>
                    )}
                  </div>
                  
                  {loadingNotifications ? (
                    <div className="px-4 py-8 text-center text-gray-500 text-sm">
                      Loading notifications...
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="px-4 py-8 text-center text-gray-500 text-sm">
                      <Bell className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                      No notifications yet
                    </div>
                  ) : (
                    <div>
                      {notifications.slice(0, 10).map((notification) => (
                        <button
                          key={notification.notification_id}
                          onClick={() => handleNotificationClick(notification)}
                          className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-50 last:border-0 ${
                            !notification.read ? 'bg-purple-50' : ''
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${
                              !notification.read ? 'bg-[#80298F]' : 'bg-transparent'
                            }`} />
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${!notification.read ? 'font-semibold' : 'font-medium'} text-gray-900 truncate`}>
                                {notification.title}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                                {notification.content}
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                {formatDateTimeShort(notification.created_at)}
                              </p>
                            </div>
                            {notification.read && (
                              <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* User Profile Dropdown */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="hidden sm:flex items-center space-x-2 px-3 py-1 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                data-testid="user-menu-button"
              >
                <div className="w-8 h-8 rounded-full bg-[#80298F] flex items-center justify-center text-white text-sm font-semibold">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                  ) : (
                    user.name.charAt(0).toUpperCase()
                  )}
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-900">{user.name.split(' ')[0]}</p>
                  <p className="text-xs text-gray-500 capitalize">{user.role}</p>
                </div>
                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">{user.name}</p>
                    <p className="text-xs text-gray-500">{user.email}</p>
                  </div>
                  
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      navigate('/settings/notifications');
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    data-testid="notification-settings-link"
                  >
                    <Settings className="h-4 w-4 text-gray-400" />
                    Notification Settings
                  </button>
                  
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      navigate('/settings/change-password');
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    data-testid="change-password-link"
                  >
                    <Lock className="h-4 w-4 text-gray-400" />
                    Change Password
                  </button>
                  
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      navigate('/help');
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    data-testid="help-link"
                  >
                    <HelpCircle className="h-4 w-4 text-gray-400" />
                    Help & FAQ
                  </button>
                  
                  <div className="border-t border-gray-100 my-1"></div>
                  
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      onLogout();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    data-testid="dropdown-logout-button"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
            
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onLogout}
              data-testid="logout-button"
              className="text-gray-600 hover:text-[#DC3239]"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Mobile Navigation Drawer */}
        {showMobileMenu && (
          <div className="lg:hidden fixed inset-0 z-40" style={{ top: '64px' }}>
            {/* Backdrop */}
            <div 
              className="absolute inset-0 bg-black/20"
              onClick={() => setShowMobileMenu(false)}
            />
            {/* Drawer */}
            <nav className="absolute left-0 top-0 w-64 h-full bg-white shadow-xl border-r border-gray-200 py-4">
              <div className="px-4 pb-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#80298F] flex items-center justify-center text-white font-semibold">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
                    ) : (
                      user.name.charAt(0).toUpperCase()
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{user.name}</p>
                    <p className="text-xs text-gray-500 capitalize">{user.role}</p>
                  </div>
                </div>
              </div>
              
              <div className="py-2">
                {visibleItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <button
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className={`w-full flex items-center gap-3 px-4 py-3 transition-all ${
                        active
                          ? 'bg-[#80298F] text-white'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="font-medium">{item.label}</span>
                    </button>
                  );
                })}
              </div>
              
              <div className="border-t border-gray-100 py-2">
                <button
                  onClick={() => navigate('/settings/notifications')}
                  className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50"
                >
                  <Settings className="h-5 w-5" />
                  <span>Notification Settings</span>
                </button>
                <button
                  onClick={() => navigate('/settings/change-password')}
                  className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50"
                >
                  <Lock className="h-5 w-5" />
                  <span>Change Password</span>
                </button>
              </div>
              
              <div className="border-t border-gray-100 py-2">
                <button
                  onClick={onLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50"
                >
                  <LogOut className="h-5 w-5" />
                  <span>Sign Out</span>
                </button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}

export default Navigation;